import React, { useCallback, useEffect } from 'react';
import usePaging from 'new/state/paging/repository/usePaging';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import useFlows from 'new/components/flows/useFlows';
import EditDynamicDataMap from 'js/components/destination/destination-content/EditDynamicDataMap';
import { CONNECTION_CATEGORY_DATA_MAP } from 'js/constants/connectionCategories';
import useApp from 'new/state/app/repository/useApp';
import { useHistory } from 'react-router-dom';
import useFlowParams from '../../useFlowParams';

type DynamicDataMapProps = {
  formType: string;
  flowType: string;
  sink?: any;
  nexset?: any;
  nexsetSchema?: any;
  sinkConfig?: any;
  selectedConnection: string;
  isEditMode: boolean;
  onSinkConfigChanged?(sinkConfig: any, field: string, value: any): void;
  onSaveAdvancedMode(data: any): void;
  nextFormStage(...args: any): any;
  onError?(msg: string): void;
};

export const DynamicDataMap = (props: DynamicDataMapProps) => {
  const {
    sink,
    nexset,
    nexsetSchema,
    formType,
    flowType,
    sinkConfig,
    selectedConnection,
    isEditMode,
    onSinkConfigChanged,
  } = props;

  const ctx = useApp();
  const history = useHistory();
  const { getSinkConfigFields } = usePaging();
  const { data, updateFormData: originalUpdateFormData } = useFlowsSetup(formType, flowType);
  const { onSinkMappingUpdated } = useFlows(originalUpdateFormData);

  const { isModal, onDismiss } = useFlowParams();

  const sinkConfigFields = getSinkConfigFields(selectedConnection);

  useEffect(() => {
    if (isEditMode && sink) {
      const initialConfig = {
        ...sinkConfig,
        name: sink.name,
        description: sink.description,
        data_map: {
          ...sink.sink_config.data_map,
          map_primary_key: sink.sink_config.data_map.map_primary_key,
          data_defaults: sink.sink_config.data_map.data_defaults || {},
        },
      };

      originalUpdateFormData({
        ...data,
        sinkConfig: initialConfig,
      });

      onSinkConfigChanged(initialConfig, null, null);
    }
  }, [isEditMode, sink]);

  const handleFormDataUpdate = (formType: string, formData: any) => {
    if (formData.dataMapSettings) {
      const newConfig = {
        ...sinkConfig,
        name: formData.dataMapSettings.name,
        description: formData.dataMapSettings.description,
        data_map: {
          ...sinkConfig.data_map,
          map_primary_key: formData.dataMapSettings.map_primary_key,
          data_defaults: Object.keys(formData.dataMapSettings)
            .filter((key) => key.startsWith('data_defaults.'))
            .reduce(
              (acc, key) => ({
                ...acc,
                [key.replace('data_defaults.', '')]: formData.dataMapSettings[key],
              }),
              {},
            ),
        },
      };

      originalUpdateFormData({
        ...data,
        sinkConfig: newConfig,
      });
    } else if (formData.sinkConfig) {
      originalUpdateFormData(formData);
    }
  };

  const handleSinkConfigChanged = (newConfig: any) => {
    const updatedConfig = {
      ...sinkConfig,
      name: newConfig.name,
      description: newConfig.description,
      data_map: {
        ...sinkConfig.data_map,
        map_primary_key: newConfig.map_primary_key,
        data_defaults: Object.keys(newConfig)
          .filter((key) => key.startsWith('data_defaults.'))
          .reduce(
            (acc, key) => ({
              ...acc,
              [key.replace('data_defaults.', '')]: newConfig[key],
            }),
            {},
          ),
      },
    };

    onSinkConfigChanged(updatedConfig, null, null);
    originalUpdateFormData({
      ...data,
      sinkConfig: updatedConfig,
    });
  };

  const dataMapSettings = {
    name: sinkConfig?.name || '',
    map_primary_key: sinkConfig?.data_map?.map_primary_key || '',
    description: sinkConfig?.description || '',
    ...Object.keys(sinkConfig?.data_map?.data_defaults || {}).reduce(
      (acc, key) => ({
        ...acc,
        [`data_defaults.${key}`]: sinkConfig.data_map.data_defaults[key],
      }),
      {},
    ),
  };

  const goBack = useCallback(() => {
    if (isModal) {
      onDismiss?.({});
      return;
    }
    history.goBack();
  }, [isModal, onDismiss, history]);

  return (
    <EditDynamicDataMap
      sinkConfigFields={sinkConfigFields}
      dataMapSettings={dataMapSettings}
      formType={formType}
      updateFormData={handleFormDataUpdate}
      sinkConfig={sinkConfig}
      selectedConnection={selectedConnection}
      dataSchema={nexsetSchema}
      setContextualSupport={ctx.setContextualSupport}
      unsetContextualSupport={ctx.unsetContextualSupport}
      isEditMode={isEditMode}
      dataSetId={nexset?.id}
      onSinkConfigChanged={handleSinkConfigChanged}
      onSinkMappingUpdated={(newSinkMapping, auto) => {
        onSinkMappingUpdated({
          sinkConfig,
          isEditMode,
          connectionCategory: CONNECTION_CATEGORY_DATA_MAP,
        })(newSinkMapping, auto);
      }}
      onSaveAdvancedMode={props?.onSaveAdvancedMode}
      nextFormStage={props?.nextFormStage}
      goBack={goBack}
      onError={props?.onError}
    />
  );
};
